import { useEffect, useRef, useState } from "react";
import { Alert, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MonitorButton, MonitorDialog, MonitorInput } from "./MonitorControls";
import { api } from "../utils/helpers";
import { stageLabel } from "../utils/stages";

export default function PromotionDiscardDialog({ spaceId, rows, candidates, disabled, onClose, onStart, onEnd }) {
  const { t } = useTranslation("freigabe");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState(false);
  const payload = useRef(null);
  const writing = useRef(false);
  const alive = useRef(true);
  const heading = useRef(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const changed = rows.some(row => !candidates.some(c => c.id === row.id && !c.blockedReason));
  async function submit() {
    if (writing.current || disabled || (!uncertain && (changed || reason.trim().length < 3))) return;
    writing.current = true; setBusy(true); setError(false); onStart();
    payload.current ||= { spaceId, requestId: window.crypto.randomUUID(), candidateIds: rows.map(row => row.id), reason: reason.trim() };
    try {
      const result = await api("/api/cicd/transport/promotions/discard", { method: "POST", body: JSON.stringify(payload.current) });
      if (result?.request?.status !== "discarded") throw new Error("Invalid discard response");
      if (alive.current) { await onEnd(); onClose(true); }
    } catch (failure) {
      if (alive.current) {
        setError(true);
        setUncertain(!(failure.status >= 400 && failure.status < 500 && ![408, 429].includes(failure.status)));
        await onEnd();
      }
    } finally {
      writing.current = false; if (alive.current) setBusy(false);
    }
  }
  return <MonitorDialog className="promotion-dialog promotion-discard-dialog" maxWidth="sm" aria-labelledby="discard-title"
    aria-describedby="discard-description" TransitionProps={{ onEntered: () => heading.current?.focus() }}
    onClose={() => { if (!busy && !uncertain) onClose(); }}>
    <DialogTitle id="discard-title" ref={heading} tabIndex={-1}>{t("promotion.discardTitle")}</DialogTitle>
    <DialogContent><p id="discard-description">{t("promotion.discardHint")}</p>
      <ul className="promotion-discard-scope">{rows.map(row => <li key={row.id}>
        <strong>{row.packageName || row.packageId}</strong>
        {row.packageName && row.packageName !== row.packageId && <span>{row.packageId}</span>}
        <span>{row.sourceInstanceName} ({stageLabel(row.sourceStage)}) → {row.targetInstanceName} ({stageLabel(row.targetStage)})</span>
        <span>{t("promotion.artifactCount", { count: row.artifactIds?.length || 0 })}</span>
      </li>)}</ul>
      <label htmlFor="discard-reason">{t("promotion.declineNote")}</label>
      <MonitorInput id="discard-reason" fullWidth multiline minRows={2} maxLength={500} value={reason} disabled={busy || uncertain}
        onChange={event => { setReason(event.target.value); payload.current = null; }}/>
      {changed && !uncertain && <Alert severity="warning">{t("promotion.selectionChanged")}</Alert>}
      {error && <Alert severity="error">{t("promotion.discardError")}</Alert>}
      {uncertain && <Alert severity="warning">{t("promotion.discardUncertain")}</Alert>}
    </DialogContent>
    <DialogActions><MonitorButton disabled={busy || uncertain} onClick={() => onClose()}>{t("topics.cancel")}</MonitorButton>
      <MonitorButton disabled={disabled || busy || (!uncertain && (changed || reason.trim().length < 3 || reason.length > 500))} onClick={submit}>
        {t(busy ? "promotion.sending" : uncertain ? "promotion.resend" : "promotion.discard")}
      </MonitorButton></DialogActions>
  </MonitorDialog>;
}
